import { getInstance } from "@/api/core/axios-gateway";
import { TRestProductsV4, TSearchProductV4 } from "@/api/types/search-products-v4";

export type TSearchProductsPayload = {
  name: string;
  supplierId: string;
  warehouseId: string | null;
  categoryId: string[];
  brandId: string[];
  hasStock: boolean | null;
  hasOffer: boolean | null;
  featured?: boolean | null;
  segmentId?: string;
  isMarketPlace?: boolean;
  status: boolean;
  type: ("PRODUCT" | "KIT" | "GROUP")[];
  minRetailPrice?: number;
  maxRetailPrice?: number;
  minWholesalePrice?: number;
  maxWholesalePrice?: number;
  customFilterAttributes?: {
    name: string;
    operator: "EQ_IN";
    type: "LIST";
    value: string;
  }[];
  countryCode?: string;
  page: number;
  size: number;
  sort: {
    field: string;
    sort: string;
  } | null;
};

export const getSearchProductsV4 = async (payload: TSearchProductsPayload) => {
  // Migrated from products' POST /api/products/search/products to the
  // elasticsearch-gateway POST /api/search/v2/products (same endpoint
  // searchProductsES uses). The legacy handler was a thin wrapper around this
  // gateway; ES returns `total` instead of `totalElements`, so we normalize it.
  return getInstance()
    .post<TRestProductsV4 & { total?: number }>("/api/search/v2/products", payload, {
      // backoffice is an admin session (no x-supplier-id header), so the ES
      // gateway resolves the per-supplier index from this query param. The
      // gateway only honors it for ADMIN callers.
      params: payload.supplierId ? { supplierId: payload.supplierId } : undefined,
    })
    .then(res => {
      const total = res.data.total ?? res.data.totalElements;
      // Remueve duplicados
      res.data.content = res.data.content.filter(
        (value, index, self) => self.findIndex(v => v.id === value.id) === index
      );

      res.data.content = res.data.content.map(product => {
        product.type ??= "PRODUCT";
        product.products ??= [];
        return product;
      });
      return { ...res.data, totalElements: total } as TRestProductsV4;
    });
};

// searchProductsES hits the elasticsearch gateway (same endpoint suppliers-web
// uses) — POST /api/search/v2/products. The ingress strips /api/search, so the
// underlying service serves it at /v2/products. Returns the same TProductES
// shape as the v4 search endpoint, including availableWarehouses per row.
export const searchProductsES = async (
  data: Record<string, unknown>
): Promise<TRestProductsV4> => {
  return getInstance()
    .post<TRestProductsV4 & { total?: number }>(
      "/api/search/v2/products",
      data,
      {
        // Admin session has no x-supplier-id header; the ES gateway resolves the
        // per-supplier index from this query param (ADMIN-only).
        params: data.supplierId ? { supplierId: data.supplierId } : undefined,
      }
    )
    .then((res) => {
      const total = res.data.total ?? res.data.totalElements;
      return { ...res.data, totalElements: total } as TRestProductsV4;
    });
};

export const getSearchProductV4ById = async (productId: string) => {
  // Migrated from products' GET /api/products/search/product/{id} to
  // elasticsearch-gateway's GET /api/search/v2/products/{id} (the legacy
  // handler was a thin wrapper around the same gateway). Same response shape;
  // x-supplier-id is injected by the axios gateway, like the list search above.
  return getInstance()
    .get<TSearchProductV4>(`/api/search/v2/products/${productId}`)
    .then(res => {
      const product = res.data;
      product.type ??= "PRODUCT";
      product.products ??= [];
      return product;
    });
};
